import { Injectable } from '@angular/core';
import { current } from 'devextreme/ui/themes';
import { currentTheme as currentVizTheme, refreshTheme } from 'devextreme/viz/themes';
import { BehaviorSubject } from 'rxjs';

const themes = ['light', 'dark'] as const;

type Theme = (typeof themes)[number];

function getNextTheme(theme?: Theme) {
    return themes[themes.indexOf(theme) + 1] || themes[0];
}

@Injectable({
    providedIn: 'root',
})
export class ThemeService {
    private storageKey = 'app-theme';
    private readonly themeMarker = 'theme-';

    private normalizeTheme(value?: string): Theme {
        if (typeof value === 'string' && value.toLowerCase().includes('dark')) {
            return 'dark';
        }
        return 'light';
    }

    currentTheme: Theme = this.normalizeTheme(typeof globalThis.window !== 'undefined' ? globalThis.localStorage[this.storageKey] : undefined);

    public isDark = new BehaviorSubject<boolean>(this.currentTheme === 'dark');

    private getThemeStyleSheets() {
        return Array.from(document.styleSheets).filter(styleSheet => styleSheet?.href?.includes(this.themeMarker));
    }

    setAppTheme(theme = this.currentTheme) {
        this.getThemeStyleSheets().forEach(styleSheet => {
            styleSheet.disabled = !styleSheet?.href?.includes(`${this.themeMarker}${theme}`);
        });

        this.currentTheme = theme;
        this.isDark.next(this.currentTheme === 'dark');

        const regexTheme = new RegExp(String.raw`\.(${themes.join('|')})`, 'g');
        currentVizTheme(currentVizTheme().replaceAll(regexTheme, `.${theme}`));
        refreshTheme();
    }

    constructor() {
        // Apply the persisted or default theme on startup
        this.setAppTheme(this.currentTheme);
        if (typeof globalThis.window !== 'undefined') {
            globalThis.localStorage[this.storageKey] = this.currentTheme;
        }
    }

    getCurrentTheme() {
        return this.currentTheme;
    }

    isFluent(): boolean {
        return current().includes('fluent');
    }

    switchTheme() {
        const newTheme = getNextTheme(this.currentTheme);
        this.setAppTheme(newTheme);
        globalThis.localStorage[this.storageKey] = newTheme;
    }
}
